import {
  ApiErrorBody,
  CanonicalUploadRequest,
  SourceBindings,
  SubmissionResult,
  UploadMapping,
  UploadMetadata,
  UploadSchema,
  ValidationResult,
} from './types'

const orderUploadsUrl = '/api/order/uploads'
const orderMappingUrl = '/api/order/uploads/mapping/'

const drfErrorMessages = (value: unknown, path = ''): string[] => {
  if (typeof value === 'string') return [path ? `${path}: ${value}` : value]
  if (Array.isArray(value)) return value.flatMap((item) => drfErrorMessages(item, path))
  if (value && typeof value === 'object') {
    return Object.entries(value).flatMap(([field, item]) =>
      drfErrorMessages(
        item,
        field === 'detail' || field === 'non_field_errors' ? path : path ? `${path}.${field}` : field
      )
    )
  }
  return []
}

export class UploadApiError extends Error {
  status: number
  body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    const explicitError =
      body.error === 'validation_failed' ? 'Submission found issues that must be corrected.' : body.error
    const explicitErrors = body.errors?.join(', ')
    const message = explicitError || explicitErrors || drfErrorMessages(body).join('; ')
    super(message || `Request failed (${status})`)
    this.status = status
    this.body = body
  }
}

const request = async <T>(url: string, csrf: string, init: RequestInit = {}): Promise<{ data: T; status: number }> => {
  const response = await fetch(url, {
    credentials: 'same-origin',
    ...init,
    headers: {
      'X-CSRFToken': csrf,
      ...(init.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
      ...init.headers,
    },
  })
  const body = await response.json().catch(() => ({}))
  if (!response.ok) throw new UploadApiError(response.status, body)
  return { data: body as T, status: response.status }
}

export const getUploadSchema = (csrf: string) => request<UploadSchema>(`${orderUploadsUrl}/schema/`, csrf)

export const getUploadMapping = (csrf: string) => request<UploadMapping | null>(orderMappingUrl, csrf)

export const replaceUploadMapping = (bindings: SourceBindings, transforms: Record<string, string>, csrf: string) =>
  request<UploadMapping>(orderMappingUrl, csrf, {
    method: 'PUT',
    body: JSON.stringify({
      upload_type: 'orders',
      adapter: 'order.configurable_item_row',
      configuration: { bindings, transforms },
    }),
  })

export const uploadLegacyOrders = (file: File, csrf: string) => {
  const body = new FormData()
  body.append('order_upload', file, file.name)
  return request<{ success: boolean; error: string }>('/api/order/upload_orders/', csrf, { method: 'POST', body })
}

export const validateSource = (
  file: File,
  bindings: SourceBindings,
  transforms: Record<string, string>,
  upload: UploadMetadata,
  warehouseId: string | number | undefined,
  csrf: string
) => {
  const body = new FormData()
  body.append('order_upload', file, file.name)
  body.append('bindings', JSON.stringify(bindings))
  body.append('transforms', JSON.stringify(transforms))
  body.append('upload', JSON.stringify(upload))
  if (warehouseId) body.append('warehouse_id', String(warehouseId))
  return request<ValidationResult>(`${orderUploadsUrl}/validate-source/`, csrf, { method: 'POST', body })
}

export const validateOrders = (payload: CanonicalUploadRequest, csrf: string) =>
  request<ValidationResult>(`${orderUploadsUrl}/validate-orders/`, csrf, {
    method: 'POST',
    body: JSON.stringify(payload),
  })

export const submitOrders = (payload: CanonicalUploadRequest, csrf: string) =>
  request<SubmissionResult | ValidationResult>(`${orderUploadsUrl}/submit/`, csrf, {
    method: 'POST',
    body: JSON.stringify(payload),
  })
